import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { Button, Divider, Grid, Typography } from '@mui/material';
import { flow, get } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { HttpStatusCode } from 'axios';
import { useOverlay } from '../../../features/Overlay';
import { FORM, STRINGS } from '../../../constants';
import { StringUtil } from '../../../utils';
import parcelSlice from '../../../redux/parcelSlice';
import { useCustomSnackbar } from '../../../features/CustomSnackbar';
import {
  FIELDS_TO_RESET_ON_PARCEL_CHANGE,
  SEARCH_FIELD_SET,
  SEARCH_FIELDS,
} from '../constants';
import { ErrorHelpers, FormHelpers, ParcelHelpers } from '../../../helpers';
import { FormInput } from '../../../components/FormInput';
import { collectionApis } from '../../../apis';
import { resetFields } from '../../../helpers/form';

const SearchParcelFields = ({
  setParcel,
  setActiveFieldSet,
  activeFieldSet,
  collectionReference,
  isContactUsSelected,
  errors,
  onParcelFound,
}) => {
  const overlay = useOverlay();
  const form = useForm();
  const dispatch = useDispatch();
  const { showError } = useCustomSnackbar();

  const [disabledSearch, setIsDisabledSearch] = useState(false);

  const { values } = form.getState();

  const parcelNumber = get(values, FORM.HELP_FIELDS.PARCEL_NUMBER.KEY);
  const postcodeOrOrderId = get(
    values,
    FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.KEY
  );
  const collectionPostcode = get(
    values,
    FORM.HELP_FIELDS.COLLECTION_POSTCODE.KEY
  );

  const isDeliverySearch = activeFieldSet === SEARCH_FIELD_SET.DELIVERY;
  const isCollectionSearch = activeFieldSet === SEARCH_FIELD_SET.COLLECTION;

  useEffect(() => {
    if (activeFieldSet === SEARCH_FIELD_SET.COLLECTION) {
      resetFields(SEARCH_FIELDS[SEARCH_FIELD_SET.DELIVERY], form);
    } else {
      resetFields(SEARCH_FIELDS[SEARCH_FIELD_SET.COLLECTION], form);
    }
  }, [activeFieldSet, form]);

  useEffect(() => {
    if (
      isContactUsSelected &&
      !parcelNumber &&
      !postcodeOrOrderId &&
      !collectionReference &&
      !collectionPostcode
    ) {
      setActiveFieldSet(null);
    }
  }, [
    parcelNumber,
    postcodeOrOrderId,
    collectionReference,
    collectionPostcode,
    setActiveFieldSet,
    isContactUsSelected,
  ]);

  const handleError = useCallback(
    error => {
      setParcel(null);

      if (
        !ErrorHelpers.isNotFoundError(error) &&
        !ErrorHelpers.isBadRequestError(error)
      ) {
        if (isCollectionSearch) {
          return showError({
            message: STRINGS.FETCHING_COLLECTION_REFERENCE_ERROR,
          });
        }

        if (isDeliverySearch) {
          return showError({ message: STRINGS.FETCHING_PARCEL_ERROR });
        }
      } else {
        throw error;
      }
    },
    [isCollectionSearch, isDeliverySearch, setParcel, showError]
  );

  const searchParcelByDelivery = useCallback(async () => {
    const { parcelCode, accessLevel } = await dispatch(
      parcelSlice.actions.fetchParcelCodeByParcelNumber({
        parcelNumber,
        postcodeOrOrderId,
      })
    ).unwrap();

    if (ParcelHelpers.isGuestAccessLevel(accessLevel)) {
      setParcel(null);

      return;
    }

    if (parcelCode) {
      const data = await dispatch(
        parcelSlice.actions.fetchParcelByCode({
          parcelCode,
          postcodeOrOrderId,
        })
      ).unwrap();

      setParcel(data);
      if (onParcelFound) {
        await onParcelFound(data);
      }
    }
  }, [dispatch, onParcelFound, parcelNumber, postcodeOrOrderId, setParcel]);

  const fetchParcelByCollection = useCallback(async () => {
    const response = await collectionApis.getCollectionStatus(
      collectionReference,
      collectionPostcode
    );

    if (response?.data?.totalResults === 0) {
      throw {
        statusCode: HttpStatusCode.NotFound,
      };
    }

    const status = response?.data?.collectionDetails?.status;

    if (status) {
      setParcel({});

      if (onParcelFound) {
        await onParcelFound({ status });
      }
    }
  }, [collectionPostcode, collectionReference, onParcelFound, setParcel]);

  const handleFetch = useCallback(async () => {
    if (parcelNumber || (collectionReference && collectionPostcode)) {
      try {
        overlay.show();

        setIsDisabledSearch(true);
        resetFields(FIELDS_TO_RESET_ON_PARCEL_CHANGE, form);

        if (isDeliverySearch) {
          await searchParcelByDelivery();
        } else if (isCollectionSearch) {
          await fetchParcelByCollection();
        }
      } catch (error) {
        handleError(error);
      } finally {
        overlay.hide();
      }
    }
  }, [
    parcelNumber,
    collectionReference,
    overlay,
    form,
    searchParcelByDelivery,
    handleError,
    collectionPostcode,
    fetchParcelByCollection,
    isDeliverySearch,
    isCollectionSearch,
  ]);

  const handleFieldChange = useCallback(
    fieldSet => {
      setActiveFieldSet(fieldSet);
      setIsDisabledSearch(false);
    },
    [setActiveFieldSet, setIsDisabledSearch]
  );

  return (
    <Grid container spacing={2}>
      <Grid item md={6} xs={12}>
        <FormInput
          name={FORM.HELP_FIELDS.PARCEL_NUMBER.KEY}
          label={FORM.HELP_FIELDS.PARCEL_NUMBER.LABEL}
          fullWidth
          size='small'
          required={isDeliverySearch}
          onChange={() => {
            handleFieldChange(SEARCH_FIELD_SET.DELIVERY);
          }}
          fieldProps={{
            parse: flow([
              StringUtil.replaceLetters,
              ParcelHelpers.normalizeParcelNumber,
            ]),
          }}
          disabled={isCollectionSearch}
        />
      </Grid>
      <Grid item md={6} xs={12}>
        <FormInput
          name={FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.KEY}
          label={FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.LABEL}
          fullWidth
          required={isDeliverySearch}
          size='small'
          onChange={() => {
            handleFieldChange(SEARCH_FIELD_SET.DELIVERY);
          }}
          fieldProps={{
            parse: StringUtil.trimFirstAndLastSpaces,
            ...FormHelpers.fieldTrimOnBlur,
          }}
          disabled={isCollectionSearch}
          maxLength={20}
        />
      </Grid>
      {isContactUsSelected && (
        <>
          <Grid
            item
            container
            md={12}
            xs={12}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Divider sx={{ flexGrow: '1' }} />
            <Typography
              variant='body2'
              color='primary.gray_400'
              sx={{ paddingInline: 1, fontSize: '12px', lineHeight: '1' }}
            >
              or
            </Typography>
            <Divider sx={{ flexGrow: '1' }} />
          </Grid>
          <Grid item container spacing={2}>
            <Grid item md={6} xs={12}>
              <FormInput
                name={FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY}
                label={FORM.HELP_FIELDS.COLLECTION_REFERENCE.LABEL}
                fullWidth
                required={isCollectionSearch}
                size='small'
                onChange={() => {
                  handleFieldChange(SEARCH_FIELD_SET.COLLECTION);
                }}
                fieldProps={{
                  parse: flow([
                    StringUtil.replaceLetters,
                    ParcelHelpers.normalizeCollectionReferenceNumber,
                  ]),
                }}
                disabled={isDeliverySearch}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormInput
                name={FORM.HELP_FIELDS.COLLECTION_POSTCODE.KEY}
                label={FORM.HELP_FIELDS.COLLECTION_POSTCODE.LABEL}
                fullWidth
                required={isCollectionSearch}
                size='small'
                onChange={() => {
                  handleFieldChange(SEARCH_FIELD_SET.COLLECTION);
                }}
                fieldProps={{
                  parse: StringUtil.trimFirstAndLastSpaces,
                  ...FormHelpers.fieldTrimOnBlur,
                }}
                disabled={isDeliverySearch}
                maxLength={8}
              />
            </Grid>
          </Grid>
        </>
      )}
      <Grid item container sx={{ justifyContent: 'flex-end', width: '100%' }}>
        <Button
          variant='contained'
          type='button'
          size='medium'
          onClick={handleFetch}
          disabled={
            !!errors?.[FORM.HELP_FIELDS.PARCEL_NUMBER.KEY] ||
            !!errors?.[FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.KEY] ||
            !!errors?.[FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY] ||
            !!errors?.[FORM.HELP_FIELDS.COLLECTION_POSTCODE.KEY] ||
            disabledSearch ||
            !activeFieldSet
          }
          sx={{ height: 40 }}
        >
          {STRINGS.SEARCH}
        </Button>
      </Grid>
    </Grid>
  );
};

export default SearchParcelFields;

SearchParcelFields.propTypes = {
  parcelNumber: PropTypes.string,
  postcode: PropTypes.string,
  setParcel: PropTypes.func.isRequired,
  activeFieldSet: PropTypes.oneOf([
    SEARCH_FIELD_SET.COLLECTION,
    SEARCH_FIELD_SET.DELIVERY,
    null,
  ]),
  setActiveFieldSet: PropTypes.func.isRequired,
  isContactUsSelected: PropTypes.bool,
  collectionReference: PropTypes.string,
  onParcelFound: PropTypes.func,
  errors: PropTypes.object,
};
